/**
 * External dependencies
 */
import React, { memo, useMemo } from 'react';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@naruto/naruto-theme/src/common';
import ErrorBound from '@naruto/naruto-components/src/common/v1/ErrorBound';
import MainCheckView from '@naruto/naruto-components/src/layer/v1/CheckView';
import ModalConfirm from '@naruto/naruto-components/src/common/v1/ModalConfirm';
import ModalDialog from '@naruto/naruto-components/src/common/v1/ModalDialog';
import MessageSnackbar from '@naruto/naruto-components/src/common/v1/MessageSnackbar';
import { AppRouter } from '@naruto/naruto-hooks/src/useRouter';

/**
 * Internal dependencies
 */
import MainView from '@naruto/bgdcms/src/components/Layer/MainView';
import useTheme from '@naruto/bgdcms/src/hooks/useTheme';
import useData, { DEFAULT_RESIZE } from './useData';

function App(): React.ReactElement {
  /* Global & local State */
  const theme = useTheme();
  const {
    userAuthCheck,
    messageDialogList,
    messageConfirmList,
    messageSnackList,
    containerRef,
    targetRef,
    resize,
    removeDialog,
    removeConfirm,
    removeSnack
  } = useData();

  /* Views */
  const renderMainView = useMemo(() => {
    if (userAuthCheck) {
      return (
        <AppRouter basename="">
          <ScopedCssBaseline ref={containerRef}>
            <Box
              ref={targetRef}
              sx={{
                minWidth: `${DEFAULT_RESIZE}px`,
                height: `calc(100% / ${resize.x})`,
                transformOrigin: 'left top',
                transform: `scale(${resize.x})`
              }}
            >
              <MainView />
            </Box>
            <ModalDialog list={messageDialogList.list} removeDialog={removeDialog} />
            <ModalConfirm list={messageConfirmList.list} removeConfirm={removeConfirm} />
            <MessageSnackbar list={messageSnackList.list} removeSnack={removeSnack} />
          </ScopedCssBaseline>
        </AppRouter>
      );
    }
    return <MainCheckView />;
  }, [userAuthCheck, messageConfirmList, messageDialogList, messageSnackList, targetRef, containerRef, resize]);

  /* Main */
  return (
    <ErrorBound>
      <ThemeProvider theme={theme}>{renderMainView}</ThemeProvider>
    </ErrorBound>
  );
}

export default memo(App);
